import { useNavigate, useParams } from "react-router-dom";
import {
  answerDisplay,
  answerIsSelected,
  stripUnnecessaryMarkup,
  updateAnswerSelections,
  useAssessmentEvent,
  useCompleteAssessmentEvent,
  useCompleteAssessmentRequest
} from "../assessmentEventUtils";
import {
  Alert,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import DataSaverOffIcon from "@mui/icons-material/DataSaverOff";
import ChatIcon from "@mui/icons-material/Chat";
import BackIcon from "@mui/icons-material/ArrowBackIos";
import NextIcon from "@mui/icons-material/ArrowForwardIos";
import Grid from "@mui/material/Unstable_Grid2";
import { APIError } from "../../../common-ui/APIError";
import { useQuestionsByInstrument } from "../../Questions/questionUtils";
import { PageWrapper, PageWrapperProps } from "../../../common-ui/PageWrapper";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssessmentCard } from "../../Assessments/AssessmentCard";
import { ProgramRole, Question, SubmittedAnswer } from "../../../API";
import { useAppSelector } from "../../../app/hooks";
import { selectCurrentUser } from "../../Users/usersSlice";
import { AnswerOptionCard } from "../../../common-ui/AnswerOptionCard";
import { LoadingButton } from "@mui/lab";
import { OptionCard } from "../../../common-ui/OptionCard";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../../common-ui/toastSlice";
import { createAssessmentNotifications } from "../../../app/constants";
import { useBlockerWithConfirmation } from "../../../common-ui/ConfirmNav";
import { Mixpanel } from "../../Analytics/Mixpanel";
import { MixpanelEvents } from "../../../app/constants";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../../Firebase/firebaseSlice";
import { useUserById } from "../../Users/userUtils";
import {
  QuestionAnswerOption,
  InputType,
  AnswerOption
} from "../assessmentEventsAPI";

type CompleteAssessmentStep = QuestionAnswerOption | string;

const returnSortedAnswerOptions = (step: CompleteAssessmentStep) => {
  if (typeof step === "string") return;
  const answerOptions = step.answerOptions;
  return answerOptions;
};

const stepIsRequired = (step: CompleteAssessmentStep) => {
  if (typeof step === "string") return false;
  const inputType = step.inputTypeName;
  if (
    inputType === InputType.Radio ||
    inputType === InputType.Checkbox ||
    (inputType === InputType.Feedback && step.required)
  )
    return true;
  return false;
};

const pageDetails: PageWrapperProps = {
  className: "CompleteAssessmentEvent",
  headerText: "Complete Assessment Event"
};

export const CompleteAssessmentEvent = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const { assessmentEventId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    assessmentDetails,
    status: assessmentEventStatus,
    error: assessmentEventError
  } = useAssessmentEvent(assessmentEventId);

  const flags = useAppSelector(selectFeatureFlags);
  const firebaseStatus = useAppSelector(selectFeatureFlagsStatus);
  const firebaseError = useAppSelector(selectFeatureFlagsError);

  const {
    status: completeAssessmentRequestStatus,
    error: completeAssessmentRequestError,
    completeAssessmentRequest
  } = useCompleteAssessmentRequest();
  const { activity_notes_visible } =
    flags;

  const sortedQuestions = assessmentDetails?.questions ?? [];
  const steps: CompleteAssessmentStep[] = [...(sortedQuestions ?? [])];
  steps.push("Summary");

  const necessaryDataStatuses = [assessmentEventStatus, firebaseStatus];
  const necessaryDataErrors = [assessmentEventError, firebaseError];

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [activeStepPrompt, setActiveStepPrompt] = useState<string>("");
  const [answerProvidedForActiveStep, setAnswerProvidedForActiveStep] =
    useState(false);
  const activeStep = steps[activeStepIndex];
  const activeStepIsQuestion = typeof activeStep !== "string";
  const activeStepQuestion = sortedQuestions[activeStepIndex];
  const onFinalStep = activeStepIndex === steps.length - 1;

  const [answers, setAnswers] = useState<AnswerOption[]>([]);
  const answersForCurrentQuestion = answers.filter(
    (answer) =>
      activeStepIsQuestion && answer.questionId === activeStepQuestion.id
  );

  const shouldBlock =
    answers.length > 0 &&
    answers.some((answer) => answer) &&
    ![completeAssessmentRequestStatus].includes("succeeded");

  useBlockerWithConfirmation({
    title: createAssessmentNotifications.NavTitle,
    prompt: createAssessmentNotifications.NavPrompt,
    confirmText: createAssessmentNotifications.NavConfirm,
    cancelText: createAssessmentNotifications.NavCancel,
    shouldBlock: shouldBlock
  });

  const activeStepAnswerOptions = returnSortedAnswerOptions(activeStep);

  const activeStepRequired = stepIsRequired(activeStep);

  const handleBack = () => {
    setActiveStepIndex(activeStepIndex - 1);
  };

  const handleNext = () => {
    if (onFinalStep) {
      if (assessmentDetails) {
        const submittedAnswers: SubmittedAnswer[] = answers
          .map((answer) => {
            if (answer.questionId && answer.value) {
              const submittedAnswer: SubmittedAnswer = {
                questionId: answer.questionId,
                value: answer.value
              };
              return submittedAnswer;
            }
          })
          .flatMap((submittedAnswer) =>
            submittedAnswer ? [submittedAnswer] : []
          );
          completeAssessmentRequest(
            assessmentDetails.assessmentEventId,
            submittedAnswers
          );
      }
    } else {
      setActiveStepIndex(activeStepIndex + 1);
    }
  };

  useEffect(() => {
    if (activeStepIsQuestion) {
      setActiveStepPrompt(activeStep.prompt ?? "N/A");
      const answerForCurrentQuestion = !!answers.find(
        (answer) => answer.questionId === activeStepQuestion?.id
      );
      setAnswerProvidedForActiveStep(answerForCurrentQuestion);
    }
  }, [activeStep, activeStepIsQuestion, answers]);

  useEffect(() => {
    if ([completeAssessmentRequestStatus].includes("succeeded")) {
      dispatch(setToastMessage("Assessment has been successfully completed."));
      dispatch(setToastStyle("success"));
      dispatch(setShowToast(true));
      Mixpanel.track(MixpanelEvents.AssessmentRatingCompleted);
      if (!shouldBlock) {
        navigate("/assessments/pending");
      }
    }
    if ([completeAssessmentRequestStatus].includes("failed")) {
      dispatch(
        setToastMessage(
          `There has been an error completing the assessment: ${
            completeAssessmentRequestError ||
            "Unknown Error"
          }`
        )
      );
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [
    completeAssessmentRequestStatus,
    completeAssessmentRequestError,
    answers,
    shouldBlock
  ]);

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.AssessmentRatingStarted);
  }, []);

  if (necessaryDataStatuses.includes("loading")) return <CircularProgress />;

  if (necessaryDataStatuses.includes("failed")) {
    const firstError = necessaryDataErrors[0];
    return <APIError error={firstError || "API Error"} />;
  }

  if (
    necessaryDataStatuses.every((status) => status === "succeeded") &&
    assessmentDetails
  ) {
    if (assessmentDetails.assessmentEventDeclined) {
      return (
        <Alert severity="info">{t("This assessment has been declined.")}</Alert>
      );
    }
    if (assessmentDetails.assessmentEventCompletionDate) {
      return (
        <Alert severity="info">
          {t("This assessment has already been completed.")}
        </Alert>
      );
    }
    return (
      <PageWrapper {...pageDetails}>
        <Grid
          container
          xs={12}
          mdOffset={2}
          md={8}
          p={2}
          spacing={2}
          sx={{ display: "flex", flex: 1, minHeight: 0 }}
        >
          <Grid xs={12}>
            <Stepper activeStep={activeStepIndex}>
              {steps.map((step) => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: { optional?: ReactNode } = {};
                const isQuestion = typeof step !== "string";
                return (
                  <Step
                    key={isQuestion ? step.id : step}
                    {...stepProps}
                    sx={{
                      "& .MuiStepLabel-root .Mui-completed": {
                        color: "primary.main"
                      },
                      "& .MuiStepLabel-root .Mui-active": {
                        color: "secondary.main"
                      }
                    }}
                  >
                    <StepLabel {...labelProps}>
                      {t(isQuestion ? step.title ?? "" : step)}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
          <Grid xs={12} md={10} mdOffset={1} spacing={2}>
            <Grid xs={12}>
              <AssessmentCard
                assessmentDetails={assessmentDetails}
                onClick={() => null}
                selfAssessment={
                  assessmentDetails?.assessmentEventSubjectUserId === currentUser?.id
                }
              />
            </Grid>
            {activity_notes_visible && assessmentDetails.activityEventNotes && (
              <Grid xs={12}>
                <Typography variant="subtitle2" color="text.primary">
                  {t(
                    "Note from " +
                      assessmentDetails?.user.fullName +
                      " about this encounter"
                  )}
                  :{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {assessmentDetails.activityEventNotes}
                    </Typography>
                  </Typography>
                </Grid>
              )}
            {activeStepIsQuestion && (
              <>
                {/* Prompt */}
                <Grid xs={12} display="flex" justifyContent="center">
                  <Typography variant="h6">{t(activeStepPrompt)}</Typography>
                </Grid>

                {/* Help Text */}
                {activeStep.helpText && (
                  <Grid xs={12} display="flex" justifyContent="center">
                    <Typography
                      color={"#00000099"}
                      dangerouslySetInnerHTML={{
                        __html: stripUnnecessaryMarkup(activeStep.helpText)
                      }}
                    />
                  </Grid>
                )}
              </>
            )}
            {activeStepIsQuestion && (
              <>
                {activeStep.inputTypeName &&
                  [InputType.Radio, InputType.Checkbox].includes(
                    activeStep.inputTypeName
                  ) &&
                  activeStepAnswerOptions &&
                  activeStepAnswerOptions.map((answerOption) => {
                    if (answerOption) {
                      const onClick = () => {
                        updateAnswerSelections({
                          question: activeStepQuestion,
                          currentAnswers: answers,
                          setAnswers,
                          answerOption
                        });
                      };
                      return (
                        <Grid xs={12} key={answerOption.id}>
                          <AnswerOptionCard
                            inputType={activeStep.inputTypeName ?? InputType.Radio}
                            answerOption={answerOption}
                            selected={answerIsSelected(answerOption, answers)}
                            onClick={onClick}
                          />
                        </Grid>
                      );
                    }
                  })}
                {activeStep.inputTypeName === InputType.Feedback && (
                  <Grid xs={12}>
                    <TextField
                      key={activeStepIndex}
                      label={activeStep.title}
                      value={answersForCurrentQuestion[0]?.value || ""}
                      onChange={(event) => {
                        updateAnswerSelections({
                          question: activeStepQuestion,
                          currentAnswers: answers,
                          setAnswers,
                          stringValue: event.target.value
                        });
                      }}
                      multiline
                      rows={4}
                      fullWidth
                    />
                  </Grid>
                )}
              </>
            )}
            {onFinalStep && (
              <>
                {sortedQuestions.map((question) => {
                  if (question.inputTypeName === InputType.NoInput) return;
                  const selectedAnswers = answers.filter(
                    (answer) => answer.questionId === question.id
                  );
                  const sortedSelectedAnswers = selectedAnswers.sort(
                    (a, b) => (a.sortingIndex || 0) - (b.sortingIndex || 0)
                  );
                  const answerToDisplay = answerDisplay(
                    question,
                    sortedSelectedAnswers
                  );
                  const avatar =
                    question.inputTypeName === InputType.Feedback ? (
                      <ChatIcon />
                    ) : (
                      <DataSaverOffIcon />
                    );
                  return (
                    <Grid xs={12} key={question.id}>
                      <OptionCard
                        avatarContent={avatar}
                        title={question.title || "N/A"}
                        subtitle={answerToDisplay}
                        filledBackground
                        bordered
                      />
                    </Grid>
                  );
                })}
              </>
            )}
            <Grid xs={12} display="flex" justifyContent="center">
              <Grid>
                <Button
                  onClick={handleBack}
                  disabled={
                    activeStepIndex === 0 ||
                    [completeAssessmentRequestStatus].includes("loading")
                  }
                  startIcon={<BackIcon />}
                  variant="outlined"
                >
                  {t("BACK")}
                </Button>
              </Grid>
              <Grid>
                <LoadingButton
                  onClick={handleNext}
                  endIcon={<NextIcon />}
                  variant={onFinalStep ? "contained" : "outlined"}
                  loading={[completeAssessmentRequestStatus].includes(
                    "loading"
                  )}
                  disabled={
                    (activeStepRequired && !answerProvidedForActiveStep) ||
                    [completeAssessmentRequestStatus].includes("loading")
                  }
                >
                  {t(onFinalStep ? "SAVE & FINISH" : "NEXT")}
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </PageWrapper>
    );
  }

  return null;
};
